/**
 * Base LLM Provider - v13 Section 6.10-6.11
 *
 * Abstract base class and shared utilities for LLM providers.
 * Extracted from @ownyou/iab-classifier for Sprint 2 consolidation.
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
/**
 * Supported LLM provider identifiers
 */
export var LLMProviderType;
(function (LLMProviderType) {
    LLMProviderType["OPENAI"] = "openai";
    LLMProviderType["ANTHROPIC"] = "anthropic";
    LLMProviderType["GOOGLE"] = "google";
    LLMProviderType["GROQ"] = "groq";
    LLMProviderType["DEEPINFRA"] = "deepinfra";
    LLMProviderType["OLLAMA"] = "ollama";
    LLMProviderType["WEBLLM"] = "webllm";
    LLMProviderType["MOCK"] = "mock";
})(LLMProviderType || (LLMProviderType = {}));
/**
 * Create a console logger with class name prefix
 */
export function createLogger(className) {
    const prefix = `[${className}]`;
    return {
        error: (message, context) => console.error(prefix, message, context ?? ''),
        warn: (message, context) => console.warn(prefix, message, context ?? ''),
        info: (message, context) => console.info(prefix, message, context ?? ''),
        debug: (message, context) => console.debug(prefix, message, context ?? ''),
    };
}
/**
 * Abstract base class for LLM providers
 *
 * Provides shared functionality for request validation, message preparation,
 * and error handling. Concrete providers extend this class.
 */
export class BaseLLMProvider {
    constructor(config) {
        this.config = config;
        this.logger = createLogger(this.constructor.name);
    }
    /**
     * Validate a request and return list of errors (empty if valid)
     */
    validateRequest(request) {
        const errors = [];
        if (!request.messages || request.messages.length === 0) {
            errors.push('At least one message is required');
            return errors;
        }
        for (let i = 0; i < request.messages.length; i++) {
            const message = request.messages[i];
            if (!message.role) {
                errors.push(`Message ${i}: role is required`);
            }
            if (!['system', 'user', 'assistant'].includes(message.role)) {
                errors.push(`Message ${i}: invalid role '${message.role}'`);
            }
            if (!message.content) {
                errors.push(`Message ${i}: content is required`);
            }
        }
        return errors;
    }
    /**
     * Prepare messages for the provider (handle system prompt if separate)
     */
    prepareMessages(messages, systemPrompt) {
        const result = [...messages];
        if (systemPrompt) {
            // Prepend system message if not already present
            const hasSystem = result.some((m) => m.role === 'system');
            if (!hasSystem) {
                result.unshift({ role: 'system', content: systemPrompt });
            }
        }
        return result;
    }
    /**
     * Create a standardized error response
     */
    createErrorResponse(error, model = 'unknown') {
        return {
            content: '',
            model,
            usage: {
                inputTokens: 0,
                outputTokens: 0,
                totalTokens: 0,
                costUsd: 0,
            },
            finishReason: 'stop',
            error,
        };
    }
    /**
     * Estimate token count from text (rough approximation: ~4 chars per token)
     */
    estimateTokens(text) {
        return Math.ceil(text.length / 4);
    }
    /**
     * Sleep for a given number of milliseconds
     */
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * Retry logic with exponential backoff
     */
    async withRetry(operation, maxRetries = 3, baseDelayMs = 1000) {
        let lastError;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await operation();
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                // Don't retry on certain errors
                if (this.isNonRetryableError(lastError)) {
                    throw lastError;
                }
                if (attempt < maxRetries) {
                    const delay = baseDelayMs * Math.pow(2, attempt);
                    this.logger.warn(`Attempt ${attempt + 1} failed, retrying in ${delay}ms`, lastError.message);
                    await this.sleep(delay);
                }
            }
        }
        throw lastError;
    }
    /**
     * Check if an error should not be retried
     */
    isNonRetryableError(error) {
        const message = error.message.toLowerCase();
        return (message.includes('invalid api key') ||
            message.includes('authentication') ||
            message.includes('unauthorized') ||
            message.includes('forbidden') ||
            message.includes('not found') ||
            message.includes('invalid request'));
    }
}
/**
 * Create a simple LLM request with a single user message
 */
export function createSimpleRequest(userMessage, systemPrompt, model, maxTokens, temperature) {
    const messages = [{ role: 'user', content: userMessage }];
    if (systemPrompt) {
        messages.unshift({ role: 'system', content: systemPrompt });
    }
    return {
        messages,
        model,
        maxTokens,
        temperature,
    };
}
/**
 * Create an LLM request from a conversation history
 */
export function createConversationRequest(conversation, systemPrompt, model, maxTokens, temperature) {
    const messages = conversation.map(([role, content]) => ({
        role: role,
        content,
    }));
    if (systemPrompt) {
        const hasSystem = messages.some((m) => m.role === 'system');
        if (!hasSystem) {
            messages.unshift({ role: 'system', content: systemPrompt });
        }
    }
    return {
        messages,
        model,
        maxTokens,
        temperature,
    };
}
